/**
 * Admin product CRUD endpoints.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.4).
 *   - GET    /admin/products             — list (optional active_only)
 *   - POST   /admin/products             — create
 *   - PATCH  /admin/products/{id}        — update
 *   - DELETE /admin/products/{id}        — soft delete
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from '../client';
import type { RuntimeProduct, SupplementProfile, SupplementProfileRecord } from '../../types/config';

// ─── Payload shapes ─────────────────────────────────────────────────────────

/** Fields shared by create + update: channel bindings (peer-level). The price/
 *  stock source is not chosen per product — it follows the funnel's default
 *  channel (backend-managed). */
interface ChannelBindingFields {
  /** Optional: a product can be bound to Shopify, PrestaShop, both or neither. */
  prestashop_id_product?: number | null;
  prestashop_id_product_attribute?: number | null;
}

export interface ProductCreateRequest extends ChannelBindingFields {
  shopify_variant_id?: string;
  shopify_product_id?: string;
  name: string;
  display_name?: string;
  protein_type?: string;
  product_type?: 'recipe' | 'supplement';
  protein_types?: string[];
  allergens?: string[];
  description?: string;
  emoji?: string;
  benefits?: string[];
  ingredients?: string;
  tags?: string[];
  nutrition_fiber?: number;
  nutrition_moisture?: number;
  kcal_per_100g?: number;
  serving_size_g?: number;
  protein_percentage?: number;
  fat_percentage?: number;
  price_eur?: number;
  is_active: boolean;
  safety_stock_units?: number;
  track_stock?: boolean;
  recipe_group?: string | null;
  pack_size_g?: number | null;
  life_stage?: 'puppy' | 'adult' | 'all' | null;
  /** Clinical supplement profile UUID (supplements only); '' / null = unlink. */
  supplement_profile_id?: string | null;
}

export interface ProductUpdateRequest extends ChannelBindingFields {
  shopify_variant_id?: string;
  shopify_product_id?: string;
  name?: string;
  display_name?: string | null;
  protein_type?: string;
  product_type?: 'recipe' | 'supplement';
  protein_types?: string[];
  allergens?: string[];
  description?: string;
  emoji?: string;
  benefits?: string[];
  ingredients?: string;
  tags?: string[];
  nutrition_fiber?: number;
  nutrition_moisture?: number;
  kcal_per_100g?: number;
  serving_size_g?: number;
  protein_percentage?: number;
  fat_percentage?: number;
  price_eur?: number;
  is_active?: boolean;
  safety_stock_units?: number;
  track_stock?: boolean;
  recipe_group?: string | null;
  pack_size_g?: number | null;
  life_stage?: 'puppy' | 'adult' | 'all' | null;
  /** Clinical supplement profile UUID (supplements only); '' / null = unlink. */
  supplement_profile_id?: string | null;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

export const adminListProducts = async (activeOnly = false): Promise<RuntimeProduct[]> => {
  const response = await apiClient.get<RuntimeProduct[]>('/admin/products', {
    params: { active_only: activeOnly },
  });
  return response.data;
};

export const adminCreateProduct = async (payload: ProductCreateRequest): Promise<RuntimeProduct> => {
  const response = await apiClient.post<RuntimeProduct>('/admin/products', payload, {});
  return response.data;
};

export const adminUpdateProduct = async (productId: string, payload: ProductUpdateRequest): Promise<RuntimeProduct> => {
  const response = await apiClient.patch<RuntimeProduct>(`/admin/products/${productId}`, payload, {});
  return response.data;
};

export const adminDeleteProduct = async (productId: string): Promise<void> => {
  await apiClient.delete(`/admin/products/${productId}`, {});
};

/** Neutral summary the per-channel sync returns (price/image/stock snapshot). */
export interface ChannelSyncSummary {
  checked_bindings: number;
  updated_bindings: number;
  fetch_ok: boolean;
}

/** Trigger the commercial snapshot refresh for one channel on demand (same work
 *  the per-channel cron does). */
export const adminSyncChannel = async (channel: string): Promise<ChannelSyncSummary> => {
  const response = await apiClient.post<ChannelSyncSummary>(`/admin/products/sync-channel/${channel}`, {}, {});
  return response.data;
};

// ─── Supplement clinical profiles (standalone; linked to products by UUID) ────

export const adminListSupplementProfiles = async (): Promise<SupplementProfileRecord[]> => {
  const response = await apiClient.get<SupplementProfileRecord[]>('/admin/supplement-profiles');
  return response.data;
};

export const adminCreateSupplementProfile = async (payload: {
  code: string;
  name: string;
  profile: SupplementProfile;
}): Promise<SupplementProfileRecord> => {
  const response = await apiClient.post<SupplementProfileRecord>('/admin/supplement-profiles', payload, {});
  return response.data;
};

export const adminUpdateSupplementProfile = async (
  id: string,
  payload: { name?: string; profile?: SupplementProfile },
): Promise<SupplementProfileRecord> => {
  const response = await apiClient.patch<SupplementProfileRecord>(`/admin/supplement-profiles/${id}`, payload, {});
  return response.data;
};
